import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ExternalLink, Phone, Video, Plus, BookOpen } from "lucide-react";
import { toast } from "sonner";

const RecoverySection = () => {
  const [data, setData] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", facility: "", meeting_type: "AA", schedule: "", zoom_link: "" });

  const loadMeetings = () => api.get("/meetings").then(({ data }) => setMeetings(data)).catch(() => {});
  useEffect(() => {
    api.get("/support/recovery").then(({ data }) => setData(data)).catch(() => {});
    loadMeetings();
  }, []);

  const addMeeting = async () => {
    if (!form.title.trim() || !form.schedule.trim() || !form.zoom_link.trim()) { toast.info("Add a title, schedule, and Zoom link."); return; }
    try {
      await api.post("/meetings", form);
      setForm({ title: "", facility: "", meeting_type: "AA", schedule: "", zoom_link: "" });
      setShowForm(false);
      await loadMeetings();
      toast.success("Meeting added. Thank you for helping others find support.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  if (!data) return <div className="h-24 rounded-3xl bg-linen-2 animate-pulse" />;

  return (
    <div className="space-y-6" data-testid="recovery-section">
      <p className="text-ink-2 leading-relaxed max-w-3xl">{data.intro}</p>

      <div>
        <h3 className="font-serif text-2xl text-brand mb-3">Find a meeting</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {data.meeting_finders.map((m, i) => (
            <a key={i} href={m.link} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-white rounded-2xl border border-linen-3 px-5 py-4 hover:-translate-y-0.5 transition-transform" data-testid={`finder-${i}`}>
              <span className="text-ink-2 text-sm">{m.name}{m.phone ? ` · ${m.phone}` : ""}</span>
              <ExternalLink className="w-4 h-4 text-accent shrink-0" />
            </a>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-2xl text-brand">Live Zoom meetings</h3>
          <Button onClick={() => setShowForm(!showForm)} variant="outline" className="rounded-full border-brand text-brand" data-testid="add-meeting-toggle"><Plus className="w-4 h-4 mr-1" /> Add a meeting</Button>
        </div>
        {showForm && (
          <div className="bg-white rounded-3xl border border-linen-3 p-5 mb-4 space-y-3" data-testid="meeting-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Meeting title" className="rounded-2xl bg-linen border-linen-3" data-testid="meeting-title" />
              <Input value={form.facility} onChange={(e) => setForm({ ...form, facility: e.target.value })} placeholder="Facility / group (optional)" className="rounded-2xl bg-linen border-linen-3" data-testid="meeting-facility" />
              <select value={form.meeting_type} onChange={(e) => setForm({ ...form, meeting_type: e.target.value })} className="rounded-2xl bg-linen border border-linen-3 px-3 h-10 text-sm text-ink-2" data-testid="meeting-type">
                {["AA", "NA", "SMART", "Wellbriety", "Treatment", "Support"].map((t) => <option key={t}>{t}</option>)}
              </select>
              <Input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} placeholder="e.g. Daily 7pm EST" className="rounded-2xl bg-linen border-linen-3" data-testid="meeting-schedule" />
            </div>
            <Input value={form.zoom_link} onChange={(e) => setForm({ ...form, zoom_link: e.target.value })} placeholder="https://zoom.us/j/…" className="rounded-2xl bg-linen border-linen-3" data-testid="meeting-link" />
            <Button onClick={addMeeting} className="rounded-full bg-brand hover:bg-brand-hover text-linen" data-testid="meeting-submit">Add meeting</Button>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {meetings.map((m) => (
            <div key={m.id} className="bg-white rounded-2xl border border-linen-3 p-5" data-testid="meeting-item">
              <div className="flex items-center gap-2">
                <span className="text-xs bg-calm text-calm-text px-2 py-0.5 rounded-full">{m.meeting_type}</span>
                <h4 className="font-medium text-ink">{m.title}</h4>
              </div>
              {m.facility && <p className="text-xs text-ink-mute mt-1">{m.facility}</p>}
              <p className="text-sm text-ink-2 mt-1">{m.schedule}</p>
              <a href={m.zoom_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline mt-2 font-medium" data-testid="meeting-join"><Video className="w-4 h-4" /> Join on Zoom</a>
            </div>
          ))}
          {meetings.length === 0 && <p className="text-sm text-ink-mute">No live meetings yet — add the first one above.</p>}
        </div>
      </div>

      <div>
        <h3 className="font-serif text-2xl text-brand mb-3">Readings & ebooks</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {data.ebooks.map((b, i) => (
            <a key={i} href={b.link} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 bg-white rounded-2xl border border-linen-3 p-5 hover:-translate-y-0.5 transition-transform" data-testid={`ebook-${i}`}>
              <BookOpen className="w-5 h-5 text-brand mt-0.5 shrink-0" />
              <div><p className="font-medium text-ink">{b.name}</p><p className="text-sm text-ink-2">{b.desc}</p></div>
            </a>
          ))}
        </div>
        <p className="text-xs text-ink-mute mt-2">Readings link to official, authorized sources to respect copyright.</p>
      </div>

      <Accordion type="single" collapsible className="bg-white rounded-3xl border border-linen-3 px-5">
        <AccordionItem value="steps" className="border-linen-3">
          <AccordionTrigger className="font-serif text-xl text-ink" data-testid="steps-accordion">The 12 Steps</AccordionTrigger>
          <AccordionContent>
            <ol className="space-y-2 text-sm text-ink-2 leading-relaxed">{data.twelve_steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="traditions" className="border-0">
          <AccordionTrigger className="font-serif text-xl text-ink" data-testid="traditions-accordion">The 12 Traditions</AccordionTrigger>
          <AccordionContent>
            <ol className="space-y-2 text-sm text-ink-2 leading-relaxed">{data.twelve_traditions.map((s, i) => <li key={i}>{s}</li>)}</ol>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default RecoverySection;
